import Member from 'iam/models/Member'
import { GetCurrentMember } from 'iam/SessionStore'
import { PrimeUser } from 'iam/UserStore'
import { formatWithLegendPriceUnit } from 'jobs/Job'
import once from 'lodash/once'
import { action, computed, observable, makeObservable } from 'mobx'
import User from 'iam/models/User'
import Workspace from 'iam/models/Workspace'
import { Model } from 'infra/model'
import { Opportunity as OpportunityProto } from '@taxfyle/api-internal/internal/opportunity_pb'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { decimalToNumber, timestampToISO } from 'utils/grpc-util'
import { generateAvatarFallback } from 'utils/image-util'
import Consultation from 'jobs/consultations/Consultation'
import { toStream, asAtom } from '../utils/mobx-util'
import { switchMap } from 'rxjs/operators'
import { countdown } from '../utils/rx-util'
import { of } from 'rxjs'

/**
 * The routing mode used for the opportunity.
 */
enum RoutingMode {
  /**
   * The first pro to claim the job gets the job.
   */
  FirstComeFirstServed,

  /**
   * Pros will apply for jobs and be selected based upon a heuristic.
   */
  Dispatch,
}

/**
 * Opportunity model.
 */
export default class Opportunity extends Model<
  OpportunityProto.AsObject,
  Opportunity
> {
  /**
   * An observable countdown. Ticks every second in sync with clock time until
   * the scheduled dispatch time is reached. `null` indicates that no selection
   * is currently scheduled or the selection date has been reached.
   */
  private _timeUntilSelection$ = toStream(
    () => this.dateScheduledDispatch,
    true
  ).pipe(switchMap((date) => (date ? countdown(date) : of(null))))

  /**
   * MobX-representation the time until selection stream.
   */
  private _timeUntilSelection = asAtom(this._timeUntilSelection$, null)

  @observable private _isPickingUp = false
  @observable private _isCandidate = false

  id = Id.none
  @observable workspace = Workspace.none
  @observable name = ''
  @observable description = ''
  @observable client = OpportunityClient.none
  @observable legend = OpportunityLegendDescriptor.none
  @observable specs: string[] = []
  @observable scopeItems: string[] = []
  @observable infoLines: OpportunityInfoLine[] = []
  @observable dateDeadline: Date | null = null
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable dateTransmitted = DateZero()
  @observable providerCutAmount = 0
  @observable consultation: Consultation | null = null
  @observable dateScheduledDispatch: Date | null = null
  @observable routingMode = RoutingMode.FirstComeFirstServed

  @computed
  get specsOrScopeItems() {
    const isInventoryItemPricingEnabled =
      this.workspace.config.combined_pricing?.job_scope_pricing_enabled

    return isInventoryItemPricingEnabled ? this.scopeItems : this.specs
  }

  /**
   * Whether to show the deadline date according to workspace classification.
   */
  @computed
  get shouldShowDeadline() {
    const validWorkspaces = this.workspace.classification

    // on B2E workspaces always show the deadline
    return validWorkspaces === 'B2E'
  }

  /**
   * Tracks whether the current user is trying to pick up this opportunity.
   * Used to ensure we don't trigger swooping UX for the current user if
   * events come in before the pickup call succeeds.
   */
  @computed get isPickingUp() {
    return this._isPickingUp
  }

  /**
   * Indicates whether the user is a candidate for the job.
   */
  @computed
  get isCandidate() {
    return this._isCandidate
  }

  /**
   * Empty instance.
   */
  static get none() {
    return this.lazyNone()
  }

  /**
   * Constructor.
   */
  constructor(
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
    private getCurrentMember: GetCurrentMember
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Whether the current member is allowed to view the price for this opportunity.
   */
  @computed
  get canViewPrice() {
    const currentMember = this.getCurrentMember(this.workspace.id)
    return currentMember.hasSomePermissions(
      'VIEW_PRICING',
      'ADMIN_PRO_CUT_VIEW',
      'ADMIN_JOB_VIEW'
    )
  }

  /**
   * Whether to show the earnings.
   */
  @computed
  get shouldShowProviderCutAmount() {
    if (!this.canViewPrice) {
      return false
    }

    return this.providerCutAmount > 0
  }

  /**
   * Formatted provider cut amount.
   */
  @computed
  get formattedProviderCutAmount() {
    if (!this.shouldShowProviderCutAmount) {
      return ''
    }

    return formatWithLegendPriceUnit(this.legend, this.providerCutAmount)
  }

  /**
   * Indicates whether the opportunity can be applied for.
   */
  @computed
  get isDispatchOpportunity() {
    return this.routingMode === RoutingMode.Dispatch
  }

  /**
   * The number of seconds until selection for the Opportunity. Null if
   * not selecting.
   */
  @computed
  get timeUntilSelection() {
    return this._timeUntilSelection()
  }

  /**
   * Marks this opportunity as being picked up by the current user.
   *
   * @returns A function to call that undoes it.
   */
  @action.bound
  markAsPickingUp() {
    this._isPickingUp = true
    return action(() => {
      this._isPickingUp = false
    })
  }

  /**
   * Marks the user as a candidate or not for the job.
   */
  @action.bound
  markUserAsCandidate(isCandidate: boolean) {
    this._isCandidate = isCandidate
  }

  /**
   * Parse from a proto attributes object.
   *
   * @param attrs
   */
  parse(attrs: Partial<OpportunityProto.AsObject>): Partial<Opportunity> {
    return {
      id: attrs.id,
      name: attrs.name,
      description: attrs.description,
      scopeItems: attrs.jobScopeItemsList,
      specs: attrs.specsList,
      infoLines: attrs.infoLinesList?.map(OpportunityInfoLine.from),
      legend: attrs.legend
        ? OpportunityLegendDescriptor.from(attrs.legend)
        : undefined,
      providerCutAmount: attrs.providerCutAmount
        ? decimalToNumber(attrs.providerCutAmount)
        : undefined,
      dateDeadline: maybeParseDate(timestampToISO(attrs.dateDeadline)),
      dateCreated:
        maybeParseDate(timestampToISO(attrs.dateCreated)) ?? this.dateCreated,
      dateModified:
        maybeParseDate(timestampToISO(attrs.dateModified)) ?? this.dateModified,
      dateTransmitted:
        maybeParseDate(timestampToISO(attrs.dateTransmitted)) ??
        this.dateTransmitted,
      dateScheduledDispatch:
        maybeParseDate(timestampToISO(attrs.dateScheduledDispatch)) ?? null,
      workspace: this.primeWorkspace({ id: attrs.workspace?.id }),
      client: attrs.client
        ? new OpportunityClient(
            this,
            this.primeUser({
              user_id: attrs.client.userId,
              public_id: attrs.client.userPublicId,
              given_name: attrs.client.givenName?.value,
              family_name: attrs.client.familyName?.value,
              picture: attrs.client.picture?.value,
            })
          )
        : undefined,
      consultation: attrs.consultation
        ? Consultation.fromJSON({
            id: attrs.consultation.id,
            requestedLength: attrs.consultation.requestedLength,
            availability: {
              availabilityTimesList: attrs.consultation?.availabilityTimesList,
            },
          })
        : null,
      routingMode: attrs.routingMode
        ? mapRoutingMode(attrs.routingMode)
        : undefined,
    }
  }

  /**
   * Lazy none.
   *
   * @private
   */
  private static lazyNone = once(
    () =>
      new Opportunity(
        () => User.none,
        () => Workspace.none,
        () => Member.none
      )
  )
}

/**
 * Opportunity legend descriptor.
 */
export class OpportunityLegendDescriptor {
  /**
   * Constructor.
   * @param id
   * @param version
   * @param name
   * @param priceUnit
   */
  constructor(
    public id: Id,
    public version: number,
    public name: string,
    public priceUnit: string | undefined
  ) {}

  /**
   * Empty instance.
   */
  static readonly none = new OpportunityLegendDescriptor(
    Id.none,
    -1,
    '?',
    undefined
  )

  /**
   * Creates a descriptor from attributes.
   * @param attrs
   */
  static from(
    attrs: OpportunityProto.Legend.AsObject
  ): OpportunityLegendDescriptor {
    return new OpportunityLegendDescriptor(
      attrs.id,
      attrs.version,
      attrs.name,
      attrs.priceUnit?.value
    )
  }
}

/**
 * Opportunity info line.
 */
export class OpportunityInfoLine {
  constructor(public title: string, public content: string) {}

  /**
   * Creates an info line from the proto attrs.
   *
   * @param attrs
   */
  static from(attrs: OpportunityProto.InfoLine.AsObject) {
    return new OpportunityInfoLine(attrs.title, attrs.content)
  }
}

/**
 * The client on the opportunity.
 */
export class OpportunityClient {
  /**
   * Constructor.
   */
  constructor(private opportunity: Opportunity, private _user: User) {}

  /**
   * Empty instance.
   */
  static readonly none = new OpportunityClient(Opportunity.none, User.none)

  /**
   * Whether we are allowed to view this info.
   */
  @computed
  get canViewInfo() {
    return !this.opportunity.workspace.features.jobConfig.anonymousOpportunities
  }

  /**
   * The client's display name.
   */
  @computed
  get displayName() {
    if (!this.canViewInfo) {
      return ''
    }

    return this._user.displayName
  }

  /**
   * The client's avatar.
   */
  @computed
  get avatar() {
    if (!this.canViewInfo) {
      return ''
    }

    return this._user.avatar || this.avatarFallback
  }

  /**
   * The client's avatar fallback.
   * Since we only reference a `User`, we need to use the Opportunity's
   * workspace reference to get the fallback color.
   */
  @computed
  get avatarFallback() {
    if (!this.canViewInfo) {
      return ''
    }

    return generateAvatarFallback(
      this._user.displayName,
      this.opportunity.workspace.branding.colors.primary
    )
  }

  /**
   * User information for the client.
   */
  @computed
  get user() {
    return this._user
  }
}

/**
 * Maps the opportunity routing mode from the proto representation to the domain
 * one.
 */
function mapRoutingMode(protoMode: OpportunityProto.RoutingMode) {
  switch (protoMode) {
    case OpportunityProto.RoutingMode.FIRST_COME_FIRST_SERVED:
      return RoutingMode.FirstComeFirstServed
    case OpportunityProto.RoutingMode.DISPATCH:
      return RoutingMode.Dispatch
    default:
      throw new Error('Unreachable')
  }
}
